/**
 * MobileTopBar — the phone-only header of the main app.
 *
 * Below 768px the icon Sidebar goes off-canvas and the collapsed chat strip is
 * hidden (Sidebar.css / App.css), so this bar carries the two entry points they
 * provided: the menu button opens the Sidebar as a drawer, the chat button opens
 * the chat. An open chat covers this bar and has its own close button.
 *
 * Always rendered; CSS shows it on phones only. Rendering it from useBreakpoint
 * instead would work too, but CSS keeps the show/hide in the same media query as
 * the Sidebar it replaces, so the two can never disagree.
 *
 * Main app only — Event Hub renders the Sidebar too but has no top bar, by design.
 */
import { useTranslation } from 'react-i18next';
import { useChatPanel } from '../../context/ChatPanelContext';
import './MobileTopBar.css';

interface MobileTopBarProps {
  onOpenNav: () => void;
  /**
   * Workspace page only. On desktop the file tree is reopened by re-clicking the
   * active Workspace rail icon, but on phones that icon is inside the closed
   * drawer — two taps deep and undiscoverable — so the bar offers it directly.
   */
  onToggleFiles?: () => void;
}

export default function MobileTopBar({ onOpenNav, onToggleFiles }: MobileTopBarProps) {
  const { t } = useTranslation('shell');
  const { toggleChat, chatBusy } = useChatPanel();

  return (
    <header className="mobile-top-bar">
      <button className="mobile-top-bar__btn" onClick={onOpenNav} aria-label={t('nav.openMenu')}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M4 6H20M4 12H20M4 18H20" stroke="#424650" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

      <div className="mobile-top-bar__brand">
        <img className="mobile-top-bar__logo" src="/icons/avatar-rocky.png" alt="" />
        <span className="mobile-top-bar__title">Rocky</span>
      </div>

      {onToggleFiles && (
        <button className="mobile-top-bar__btn" onClick={onToggleFiles} aria-label={t('mobileTopBar.files')}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M3 6.5C3 5.67 3.67 5 4.5 5H9.5L11.5 7H19.5C20.33 7 21 7.67 21 8.5V17.5C21 18.33 20.33 19 19.5 19H4.5C3.67 19 3 18.33 3 17.5V6.5Z" stroke="#424650" strokeWidth="2" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      <button className="mobile-top-bar__btn" onClick={toggleChat} aria-label={t('mobileTopBar.openChat')}>
        {/* Same glyph as the desktop collapsed-chat strip, so it reads as the same control. */}
        <svg width="24" height="24" viewBox="0 0 28 28" fill="none" aria-hidden="true">
          <path d="M4 4H24V20H8L4 24V4Z" stroke="#424650" strokeWidth="2" strokeLinejoin="round" />
        </svg>
        {/* The agent is still replying — the user closed the chat mid-answer. */}
        {chatBusy && <span className="mobile-top-bar__busy-dot" data-testid="chat-busy-dot" />}
      </button>
    </header>
  );
}
